import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Grid,
  Tooltip,
  Collapse,
  IconButton,
  TextField,
  InputAdornment
} from '@mui/material';
import { Refresh as RefreshIcon, Compare as CompareIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, ArrowUpward as ArrowUpwardIcon, ArrowDownward as ArrowDownwardIcon, FilterList as FilterListIcon } from '@mui/icons-material';
import { api } from '../contexts/ApiContext';
import {
  FIT_TABLE_SX,
  FIT_TABLE_CONTAINER_SX,
  FitColgroup,
  buildFitColgroup,
  fitTruncateSx,
} from '../utils/fitTable';

interface Transaction {
  id: string;
  type: string;
  description: string;
  amount: number;
  currency: string;
  date: string;
  source: 'API' | 'CSV';
}

interface MatchedPair {
  api_transaction: Transaction;
  csv_transaction: Transaction;
  match_score: number;
}

interface UnifiedData {
  matched_pairs: MatchedPair[];
  unmatched_api: Transaction[];
  unmatched_csv: Transaction[];
  summary: {
    total_api: number;
    total_csv: number;
    matched_pairs: number;
    unmatched_api: number;
    unmatched_csv: number;
  };
  timestamp: string;
}

const TransactionComparisonUnifiedView: React.FC = () => {
  const [data, setData] = useState<UnifiedData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState({
    matched: true,
    unmatchedApi: true,
    unmatchedCsv: true
  });
  
  // Sorting and filtering state
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);
  const [filters, setFilters] = useState<{
    [key: string]: string;
  }>({});
  const [matchingEnabled, setMatchingEnabled] = useState(false); // Default to false - matching is slow for large datasets

  const fetchData = useCallback(async (enableMatching: boolean = false) => {
    setLoading(true);
    setError(null);

    try {
      // Skip matching by default - it's too slow for large datasets (~6,700 transactions)
      // Users can enable matching via the toggle button if needed
      const response = await api.get('/api/admin/transaction-comparison-unified', {
        timeout: enableMatching ? 180000 : 60000,  // 3 minutes if matching enabled, 1 minute otherwise
        params: {
          skip_matching: !enableMatching,  // Skip expensive matching by default
          force_matching: enableMatching   // Only force matching if explicitly enabled
        }
      });
      setData(response.data);
      setMatchingEnabled(response.data.summary?.matching_enabled || enableMatching);
    } catch (err: any) {
      console.error('Error fetching unified data:', err);
      setError(err.response?.data?.error || 'Failed to fetch unified transaction data');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData(false); // Load WITHOUT matching by default - too slow for large datasets
  }, [fetchData]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleTimeString('en-IE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatId = (id: string | null) => {
    if (!id) return 'N/A';
    return id.length > 12 ? `...${id.slice(-12)}` : id;
  };

  const formatDateTime = (date: string | null) => {
    if (!date) return 'N/A';
    try {
      const dateObj = new Date(date);
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      const dayName = dayNames[dateObj.getDay()];
      const day = dateObj.getDate().toString().padStart(2, '0');
      const month = monthNames[dateObj.getMonth()];
      const year = dateObj.getFullYear();
      const hours = dateObj.getHours().toString().padStart(2, '0');
      const minutes = dateObj.getMinutes().toString().padStart(2, '0');
      const seconds = dateObj.getSeconds().toString().padStart(2, '0');
      
      return `${dayName} ${day}-${month}-${year}, ${hours}:${minutes}:${seconds}`;
    } catch {
      return date;
    }
  };

  const formatAmount = (amount: number | null) => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const getMatchScoreColor = (score: number) => {
    if (score >= 0.9) return 'success';
    if (score >= 0.7) return 'warning';
    return 'error';
  };

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const scrollToSection = (section: string) => {
    // Close all sections first
    setExpandedSections({
      matched: false,
      unmatchedApi: false,
      unmatchedCsv: false
    });
    
    // Open the target section immediately (no delay needed)
    setExpandedSections(prev => ({
      ...prev,
      [section]: true
    }));
    
    // Scroll to the section immediately
    const element = document.getElementById(section);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Sorting functions
  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortData = (data: any[], key: string, direction: 'asc' | 'desc') => {
    return [...data].sort((a, b) => {
      let aVal = a[key];
      let bVal = b[key];
      
      // Handle nested properties
      if (key.includes('.')) {
        const keys = key.split('.');
        aVal = keys.reduce((obj, k) => obj?.[k], a);
        bVal = keys.reduce((obj, k) => obj?.[k], b);
      }
      
      // Handle dates
      if (key.includes('date') || key.includes('Date')) {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }
      
      // Handle numbers
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      // Handle strings
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return direction === 'asc' 
          ? aVal.localeCompare(bVal) 
          : bVal.localeCompare(aVal);
      }
      
      return 0;
    });
  };

  // Filtering functions
  const handleFilter = (key: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const filterData = (data: any[], filters: { [key: string]: string }) => {
    return data.filter(item => {
      return Object.entries(filters).every(([key, value]) => {
        if (!value) return true;
        
        let itemVal = item[key];
        
        // Handle nested properties
        if (key.includes('.')) {
          const keys = key.split('.');
          itemVal = keys.reduce((obj, k) => obj?.[k], item);
        }
        
        // Convert to string for comparison
        const strVal = String(itemVal || '').toLowerCase();
        return strVal.includes(value.toLowerCase());
      });
    });
  };

  // Sortable header component
  const SortableHeader: React.FC<{
    children: React.ReactNode;
    sortKey: string;
    onSort: (key: string) => void;
    sortConfig: { key: string; direction: 'asc' | 'desc' } | null;
  }> = ({ children, sortKey, onSort, sortConfig }) => {
    const isActive = sortConfig?.key === sortKey;
    const direction = isActive ? sortConfig.direction : null;
    
    return (
      <TableCell 
        sx={{ 
          cursor: 'pointer', 
          userSelect: 'none',
          '&:hover': { backgroundColor: 'action.hover' },
          fontWeight: 'bold'
        }}
        onClick={() => onSort(sortKey)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {children}
          {isActive && (
            direction === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
          )}
        </Box>
      </TableCell>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading unified transaction comparison...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => fetchData(false)} startIcon={<RefreshIcon />}>
          Retry
        </Button>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">No data available</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Unified Transaction Comparison
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {!matchingEnabled && (
            <Button
              variant="outlined"
              startIcon={<CompareIcon />}
              onClick={() => {
                setMatchingEnabled(true);
                fetchData(true);
              }}
              disabled={loading}
              color="primary"
              sx={{ whiteSpace: 'nowrap' }}
            >
              Enable Matching
            </Button>
          )}
          {matchingEnabled && (
            <>
              <Chip
                icon={<CompareIcon />}
                label="Matching Enabled"
                color="success"
                variant="outlined"
              />
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setMatchingEnabled(false);
                  fetchData(false);
                }}
                disabled={loading}
                color="secondary"
              >
                Disable Matching
              </Button>
            </>
          )}
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={() => fetchData(matchingEnabled)}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Warning for matching on large datasets */}
      {matchingEnabled && data && (data.summary.total_api + data.summary.total_csv > 1000) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Matching is enabled for a large dataset ({data.summary.total_api + data.summary.total_csv} transactions). 
          This may take 2-3 minutes to complete. Consider disabling matching for faster loading.
        </Alert>
      )}

      {/* Summary Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid sx={{ xs: 12, sm: 6, md: 2.4 }}>
          <Card sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h6" color="primary">
              {data.summary.total_api}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total API Transactions
            </Typography>
          </Card>
        </Grid>
        <Grid sx={{ xs: 12, sm: 6, md: 2.4 }}>
          <Card sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h6" color="secondary">
              {data.summary.total_csv}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total CSV Transactions
            </Typography>
          </Card>
        </Grid>
        <Grid sx={{ xs: 12, sm: 6, md: 2.4 }}>
          <Card 
            sx={{ p: 2, textAlign: 'center', cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              scrollToSection('matched');
            }}
          >
            <Typography variant="h6" color="success.main">
              {data.summary.matched_pairs}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Matched Pairs
            </Typography>
          </Card>
        </Grid>
        <Grid sx={{ xs: 12, sm: 6, md: 2.4 }}>
          <Card 
            sx={{ p: 2, textAlign: 'center', cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              scrollToSection('unmatchedApi');
            }}
          >
            <Typography variant="h6" color="warning.main">
              {data.summary.unmatched_api}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Unmatched API
            </Typography>
          </Card>
        </Grid>
        <Grid sx={{ xs: 12, sm: 6, md: 2.4 }}>
          <Card 
            sx={{ p: 2, textAlign: 'center', cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              scrollToSection('unmatchedCsv');
            }}
          >
            <Typography variant="h6" color="error.main">
              {data.summary.unmatched_csv}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Unmatched CSV
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Matched Pairs Table */}
      <Card sx={{ mb: 3 }} id="matched">
        <Box sx={{ p: 2, backgroundColor: 'grey.50', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton 
              onClick={() => toggleSection('matched')}
              size="small"
            >
              {expandedSections.matched ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                Matched Transaction Pairs ({data.summary.matched_pairs})
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0 }}>
                - Transactions that appear in both API and CSV data sources
              </Typography>
            </Box>
          </Box>
        </Box>
        <Collapse in={expandedSections.matched}>
          <Box sx={{ p: 2 }}>
            {/* Filter inputs */}
            <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                placeholder="Filter by description..."
                value={filters.description || ''}
                onChange={(e) => handleFilter('description', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FilterListIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 200 }}
              />
              <TextField
                size="small"
                placeholder="Filter by type..."
                value={filters.type || ''}
                onChange={(e) => handleFilter('type', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FilterListIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 150 }}
              />
            </Box>
            
            <TableContainer component={Paper} variant="outlined" sx={FIT_TABLE_CONTAINER_SX}>
              <Table size="small" sx={FIT_TABLE_SX}>
                <FitColgroup
                  cols={buildFitColgroup([
                    { key: 'date', pct: 9 },
                    { key: 'type', pct: 9 },
                    { key: 'description' },
                    { key: 'amount', pct: 9 },
                    { key: 'score', pct: 8 },
                    { key: 'api-data', pct: 22 },
                    { key: 'csv-data', pct: 22 },
                  ])}
                />
                <TableHead>
                  <TableRow>
                    <SortableHeader sortKey="api_transaction.date" onSort={handleSort} sortConfig={sortConfig}>
                      Date & Time
                    </SortableHeader>
                    <SortableHeader sortKey="api_transaction.type" onSort={handleSort} sortConfig={sortConfig}>
                      Type
                    </SortableHeader>
                    <SortableHeader sortKey="api_transaction.description" onSort={handleSort} sortConfig={sortConfig}>
                      Description
                    </SortableHeader>
                    <SortableHeader sortKey="api_transaction.amount" onSort={handleSort} sortConfig={sortConfig}>
                      Amount
                    </SortableHeader>
                    <SortableHeader sortKey="match_score" onSort={handleSort} sortConfig={sortConfig}>
                      Match Score
                    </SortableHeader>
                    <TableCell>API Data</TableCell>
                    <TableCell>CSV Data</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(sortConfig ? sortData(filterData(data.matched_pairs, filters), sortConfig.key, sortConfig.direction) : filterData(data.matched_pairs, filters)).map((pair, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {formatDate(pair.api_transaction.date)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatTime(pair.api_transaction.date)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={pair.api_transaction.type} 
                          size="small" 
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={fitTruncateSx} title={pair.api_transaction.description}>
                          {pair.api_transaction.description}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: pair.api_transaction.amount < 0 ? 'error.main' : 'success.main',
                            fontWeight: 'medium'
                          }}
                        >
                          {formatAmount(pair.api_transaction.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={`Match Score: ${(pair.match_score * 100).toFixed(1)}%`}>
                          <Chip 
                            label={`${(pair.match_score * 100).toFixed(0)}%`}
                            size="small"
                            color={getMatchScoreColor(pair.match_score)}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography variant="caption" color="primary" sx={{ fontWeight: 'medium' }}>
                            ID: {formatId(pair.api_transaction.id)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            <strong>Type:</strong> {pair.api_transaction.type}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            <strong>Date:</strong> {formatDateTime(pair.api_transaction.date)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            <strong>Description:</strong> {pair.api_transaction.description}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            <strong>Amount:</strong> {formatAmount(pair.api_transaction.amount)} {pair.api_transaction.currency}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography variant="caption" color="secondary" sx={{ fontWeight: 'medium' }}>
                            ID: {formatId(pair.csv_transaction.id)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            <strong>Type:</strong> {pair.csv_transaction.type}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            <strong>Date:</strong> {formatDateTime(pair.csv_transaction.date)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            <strong>Description:</strong> {pair.csv_transaction.description}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            <strong>Amount:</strong> {formatAmount(pair.csv_transaction.amount)} {pair.csv_transaction.currency}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Collapse>
      </Card>

      {/* Unmatched API Transactions */}
      {data.unmatched_api.length > 0 && (
        <Card sx={{ mb: 3 }} id="unmatchedApi">
          <Box sx={{ p: 2, backgroundColor: 'warning.light', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton 
                onClick={() => toggleSection('unmatchedApi')}
                size="small"
              >
                {expandedSections.unmatchedApi ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                  Unmatched API Transactions ({data.summary.unmatched_api})
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0 }}>
                  - Transactions found only in API data
                </Typography>
              </Box>
            </Box>
          </Box>
          <Collapse in={expandedSections.unmatchedApi}>
            <Box sx={{ p: 2 }}>
              {/* Filter inputs */}
              <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  placeholder="Filter by description..."
                  value={filters.description || ''}
                  onChange={(e) => handleFilter('description', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FilterListIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 200 }}
                />
                <TextField
                  size="small"
                  placeholder="Filter by type..."
                  value={filters.type || ''}
                  onChange={(e) => handleFilter('type', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FilterListIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 150 }}
                />
              </Box>
              
              <TableContainer component={Paper} variant="outlined" sx={FIT_TABLE_CONTAINER_SX}>
                <Table size="small" sx={FIT_TABLE_SX}>
                  <FitColgroup
                    cols={buildFitColgroup([
                      { key: 'date', pct: 14 },
                      { key: 'type', pct: 12 },
                      { key: 'description' },
                      { key: 'amount', pct: 12 },
                      { key: 'id', pct: 16 },
                    ])}
                  />
                  <TableHead>
                    <TableRow>
                      <SortableHeader sortKey="date" onSort={handleSort} sortConfig={sortConfig}>
                        Date & Time
                      </SortableHeader>
                      <SortableHeader sortKey="type" onSort={handleSort} sortConfig={sortConfig}>
                        Type
                      </SortableHeader>
                      <SortableHeader sortKey="description" onSort={handleSort} sortConfig={sortConfig}>
                        Description
                      </SortableHeader>
                      <SortableHeader sortKey="amount" onSort={handleSort} sortConfig={sortConfig}>
                        Amount
                      </SortableHeader>
                      <SortableHeader sortKey="id" onSort={handleSort} sortConfig={sortConfig}>
                        ID
                      </SortableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(sortConfig ? sortData(filterData(data.unmatched_api, filters), sortConfig.key, sortConfig.direction) : filterData(data.unmatched_api, filters)).map((tx, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {formatDate(tx.date)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatTime(tx.date)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={tx.type} size="small" color="primary" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={fitTruncateSx} title={tx.description}>
                            {tx.description}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: tx.amount < 0 ? 'error.main' : 'success.main',
                              fontWeight: 'medium'
                            }}
                          >
                            {formatAmount(tx.amount)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {formatId(tx.id)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Collapse>
        </Card>
      )}

      {/* Unmatched CSV Transactions */}
      {data.unmatched_csv.length > 0 && (
        <Card sx={{ mb: 3 }} id="unmatchedCsv">
          <Box sx={{ p: 2, backgroundColor: 'error.light', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton 
                onClick={() => toggleSection('unmatchedCsv')}
                size="small"
              >
                {expandedSections.unmatchedCsv ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                  Unmatched CSV Transactions ({data.summary.unmatched_csv})
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0 }}>
                  - Transactions found only in CSV data
                </Typography>
              </Box>
            </Box>
          </Box>
          <Collapse in={expandedSections.unmatchedCsv}>
            <Box sx={{ p: 2 }}>
              {/* Filter inputs */}
              <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  placeholder="Filter by description..."
                  value={filters.description || ''}
                  onChange={(e) => handleFilter('description', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FilterListIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 200 }}
                />
                <TextField
                  size="small"
                  placeholder="Filter by type..."
                  value={filters.type || ''}
                  onChange={(e) => handleFilter('type', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FilterListIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 150 }}
                />
              </Box>
              
              <TableContainer component={Paper} variant="outlined" sx={FIT_TABLE_CONTAINER_SX}>
                <Table size="small" sx={FIT_TABLE_SX}>
                  <FitColgroup
                    cols={buildFitColgroup([
                      { key: 'date', pct: 14 },
                      { key: 'type', pct: 12 },
                      { key: 'description' },
                      { key: 'amount', pct: 12 },
                      { key: 'id', pct: 16 },
                    ])}
                  />
                  <TableHead>
                    <TableRow>
                      <SortableHeader sortKey="date" onSort={handleSort} sortConfig={sortConfig}>
                        Date & Time
                      </SortableHeader>
                      <SortableHeader sortKey="type" onSort={handleSort} sortConfig={sortConfig}>
                        Type
                      </SortableHeader>
                      <SortableHeader sortKey="description" onSort={handleSort} sortConfig={sortConfig}>
                        Description
                      </SortableHeader>
                      <SortableHeader sortKey="amount" onSort={handleSort} sortConfig={sortConfig}>
                        Amount
                      </SortableHeader>
                      <SortableHeader sortKey="id" onSort={handleSort} sortConfig={sortConfig}>
                        ID
                      </SortableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(sortConfig ? sortData(filterData(data.unmatched_csv, filters), sortConfig.key, sortConfig.direction) : filterData(data.unmatched_csv, filters)).map((tx, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {formatDate(tx.date)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatTime(tx.date)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={tx.type} size="small" color="secondary" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={fitTruncateSx} title={tx.description}>
                            {tx.description}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: tx.amount < 0 ? 'error.main' : 'success.main',
                              fontWeight: 'medium'
                            }}
                          >
                            {formatAmount(tx.amount)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {formatId(tx.id)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Collapse>
        </Card>
      )}

      {/* Summary */}
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        Last updated: {formatDate(data.timestamp)}
      </Typography>
    </Box>
  );
};

export default TransactionComparisonUnifiedView;
